import { BreakdancePlugins } from "@/types/plugins";
import * as Twig from "twig";
import memoize from "lodash/memoize";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";
import { MemoizedFunction } from "lodash";
export type Filters = Record<string, FilterCallback[]>;

export type TwigCallback =
  | ((value: unknown) => string)
  | (((value: unknown) => string) & MemoizedFunction);

export type CustomTwigFunctions = {
  name: string;
  callback: TwigCallback;
};

export type FilterCallback = (
  value: unknown,
  arg: unknown,
  meta: Record<string, unknown> | null
) => unknown;

export function clearMemoizedTwigFunctionsCache() {
  if ("Breakdance" in window && "twigAPI" in window.Breakdance) {
    window.Breakdance.twigAPI.functions.forEach(fn => {
      if ("cache" in fn.callback) {
        fn.callback.cache.clear?.();
      }
    });
  }
}

export function initAPI(plugins: BreakdancePlugins) {
  window.Breakdance.filtersAPI = {
    filters: {},
    addFilter: function (filterName: string, filterCallback: FilterCallback) {
      if (filterName in window.Breakdance.filtersAPI.filters) {
        window.Breakdance.filtersAPI.filters[filterName].push(filterCallback);
      } else {
        window.Breakdance.filtersAPI.filters[filterName] = [filterCallback];
      }
    }
  };

  window.Breakdance.twigAPI = {
    functions: [],
    addFunction(
      functionName: string,
      twigCallback: TwigCallback,
      shouldMemoize: boolean
    ) {
      // Second argument of memoize is the resolver to create the cache key
      // By default it only caches the first argument
      const memoResolver = (...args: unknown[]) =>
        args.map(arg => jsonStringifyOrUndefined(arg)).join("_");

      window.Breakdance.twigAPI.functions.push({
        name: functionName,
        callback: shouldMemoize
          ? memoize(twigCallback, memoResolver)
          : twigCallback
      });
    }
  };

  // Execute JS code coming as a string from PHP
  plugins.forEach(({ script }) => {
    new Function(script)();
  });

  window.dispatchEvent(new Event("breakdanceFiltersAPIReady"));
  window.dispatchEvent(new Event("breakdanceActionsAPIReady"));
  window.dispatchEvent(new Event("breakdanceTwigApiReady"));

  // Initialize Twig functions, coming from PHP
  window.Breakdance.twigAPI.functions.forEach(customTwigFunction => {
    Twig.extendFunction(customTwigFunction.name, customTwigFunction.callback);
  });
}

export function applyFilters(
  filterName: string,
  value: unknown,
  arg: unknown = null,
  meta: Record<string, unknown> | null = null
) {
  if (filterName in window.Breakdance.filtersAPI.filters) {
    window.Breakdance.filtersAPI.filters[filterName].forEach(action => {
      value = action(value, arg, meta);
    });
  }

  return value;
}
